import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, Select, Spinner } from "@nutrayan/ui";
import { formatCurrency, formatIndianDate } from "@nutrayan/utils";
import { apiClient } from "@/lib/api-client";

// Contract: GET /admin/api/v1/orders?page&size → OrderSummary[].
// No server-side status filter exists in the contract yet (BLK-API-001),
// so the status filter below is applied client-side on the loaded page.
interface OrderSummary {
  id: number;
  orderNumber?: string;
  status?: string;
  paymentMethod?: string;
  grandTotalPaise?: number;
  itemCount?: number;
  placedAt?: string;
}

const STATUSES = [
  "PENDING_PAYMENT",
  "COD_PENDING_CONFIRM",
  "COD_VERIFIED",
  "CONFIRMED",
  "ALLOCATED",
  "READY_TO_SHIP",
  "SHIPPED",
  "DELIVERED",
  "COMPLETED",
  "PAYMENT_FAILED",
  "CANCELLED",
  "RETURN_REQUESTED",
  "RETURN_PENDING",
  "REFUNDED",
] as const;

export default function OrdersListPage() {
  const [status, setStatus] = useState<string>("");
  const [page, setPage] = useState(0);
  const query = useQuery({
    queryKey: ["admin", "orders", page],
    queryFn: () => apiClient.request<OrderSummary[]>(`/admin/api/v1/orders?page=${page}&size=20`),
  });

  const all = query.data ?? [];
  const rows = status ? all.filter((o) => o.status === status) : all;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-display-xl text-charcoal">Orders</h1>
        <p className="text-body text-ink">All customer orders.</p>
      </header>

      <div className="flex items-center gap-3">
        <label className="text-caption text-muted" htmlFor="status">
          Status
        </label>
        <Select
          id="status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="w-56"
        >
          <option value="">All</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll("_", " ").toLowerCase()}
            </option>
          ))}
        </Select>
      </div>

      {query.isPending ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : query.isError ? (
        <Card>
          <p className="text-body text-terracotta">Couldn&apos;t load orders.</p>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <p className="text-body text-ink">No orders match this filter.</p>
        </Card>
      ) : (
        <Card className="p-0">
          <table className="text-body-sm w-full">
            <thead className="border-charcoal/10 text-caption text-muted border-b text-left">
              <tr>
                <th className="px-4 py-2">Order</th>
                <th className="px-4 py-2">Placed</th>
                <th className="px-4 py-2">Items</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Payment</th>
                <th className="px-4 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} className="border-charcoal/5 border-b">
                  <td className="px-4 py-2 font-mono">
                    <Link to={`/orders/${o.id}`} className="text-forest underline">
                      {o.orderNumber ?? o.id}
                    </Link>
                  </td>
                  <td className="text-ink px-4 py-2">
                    {o.placedAt ? formatIndianDate(o.placedAt) : "—"}
                  </td>
                  <td className="text-ink px-4 py-2">{o.itemCount ?? "—"}</td>
                  <td className="text-ink px-4 py-2">
                    {(o.status ?? "—").replaceAll("_", " ").toLowerCase()}
                  </td>
                  <td className="text-ink px-4 py-2">{o.paymentMethod ?? "—"}</td>
                  <td className="price text-charcoal px-4 py-2 text-right">
                    {formatCurrency(o.grandTotalPaise ?? 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <div className="flex items-center gap-3">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0 || query.isPending}
        >
          Previous
        </Button>
        <span className="text-caption text-muted">Page {page + 1}</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setPage((p) => p + 1)}
          disabled={query.isPending || all.length < 20}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
